import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Package, ArrowLeft } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { loadProducts, loadCategories, loadProduct, saveProduct, deleteProduct } from './ProductManagementAPI';
import ProductForm from './ProductForm';
import ProductList from './ProductList';
import { getGlassStyles, getButtonStyles } from './ProductManagementStyles';
import { buildSearchableText, matchesAllTerms } from 'core/utils/search';

const InternalShopProductManagement = ({ shop, user, onBack, showSuccess, showError, showWarning }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pFilters, setPFilters] = useState({ category: [], status: [], manufacturer: [], source: [] });
  const [categories, setCategories] = useState([]);

  const styles = getGlassStyles(isDarkMode);
  const primaryButtonStyles = getButtonStyles(isDarkMode, 'primary');
  const secondaryButtonStyles = getButtonStyles(isDarkMode, 'secondary');

  useEffect(() => {
    handleLoadProducts();
    handleLoadCategories();
  }, [shop.id]);

  const handleLoadProducts = async () => {
    try {
      setLoading(true);
      const response = await loadProducts(shop.id, user);
      setProducts(response);
    } catch (error) {
      console.error('Failed to load products:', error);
      if (showError) {
        showError(t('admin.products.loadError'));
      } else {
        alert(t('admin.products.loadError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoadCategories = async () => {
    try {
      const response = await loadCategories(shop.id, user);
      setCategories(response);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      await saveProduct(shop.id, productData, editingProduct, user);
      
      if (showSuccess) {
        showSuccess(editingProduct ? t('admin.products.updated') : t('admin.products.saved'));
      } else {
        alert(editingProduct ? t('admin.products.updated') : t('admin.products.saved'));
      }
      setEditingProduct(null);
      setShowProductForm(false);
      await handleLoadProducts();
      await handleLoadCategories();
    } catch (error) {
      console.error('Failed to save product:', error);
      if (showError) {
        showError(t('admin.products.saveError', { error: error.message }));
      } else {
        alert(t('admin.products.saveError', { error: error.message }));
      }
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(t('admin.products.deleteConfirm', { name: productName }))) {
      return;
    }

    try {
      await deleteProduct(shop.id, productId, user);
      if (showSuccess) {
        showSuccess(t('admin.products.deleted'));
      } else {
        alert(t('admin.products.deleted'));
      }
      await handleLoadProducts();
      await handleLoadCategories();
    } catch (error) {
      console.error('Failed to delete product:', error);
      if (showError) {
        showError(t('admin.products.deleteError'));
      } else {
        alert(t('admin.products.deleteError'));
      }
    }
  };

  const handleEditProduct = async (product) => {
    try {
      // Vollstaendiges Produkt nachladen (inkl. sources/stock/texts/order_history),
      // damit die ProductForm alle Detailfelder bekommt statt der schlanken Listen-Row.
      const full = await loadProduct(shop.id, product.id, user);
      setEditingProduct(full || product);
    } catch (error) {
      console.error('Failed to load full product for edit:', error);
      // Fallback: schlanke Listen-Row verwenden
      setEditingProduct(product);
      if (showError) showError(t('admin.products.loadError'));
    }
    setShowProductForm(true);
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowProductForm(true);
  };

  // Filter-Optionen aus den geladenen Produkten (Kategorie/Hersteller/Quelle)
  const filterOptions = useMemo(() => {
    const cat = new Set(), man = new Set(), src = new Set();
    products.forEach((p) => {
      if (p.category) cat.add(p.category);
      if (p.manufacturer) man.add(p.manufacturer);
      if (p.source) src.add(p.source);
    });
    return { category: [...cat].sort(), manufacturer: [...man].sort(), source: [...src].sort() };
  }, [products]);

  const filteredProducts = products.filter(product => {
    // Volltext über alle relevanten Produktfelder; mehrere Begriffe (UND) via Trennzeichen
    const searchableText = buildSearchableText(
      product.name, product.description, product.category, product.sku,
      product.vendor_mat, product.preferred_vendor, product.fixed_vendor,
      product.unit, product.cost_center, product.purchasing_org, product.plant,
      product.price, product.currency
    );
    if (!matchesAllTerms(searchableText, searchTerm)) return false;
    if (pFilters.category.length && !pFilters.category.includes(product.category)) return false;
    if (pFilters.status.length && !pFilters.status.includes(product.is_active ? 'active' : 'inactive')) return false;
    if (pFilters.manufacturer.length && !pFilters.manufacturer.includes(product.manufacturer)) return false;
    if (pFilters.source.length && !pFilters.source.includes(product.source)) return false;
    return true;
  });

  if (showProductForm) {
    return (
      <ProductForm
        product={editingProduct}
        shop={shop}
        onSave={handleSaveProduct}
        onCancel={() => {
          setEditingProduct(null);
          setShowProductForm(false);
        }}
        existingCategories={categories}
        showWarning={showWarning}
      />
    );
  }

  const renderHeader = () => (
    <div className="relative group mb-6">
      <div className={styles.glassBackground}></div>
      <div className={styles.glassOverlay}></div>
      
      <div className="relative p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <button
              onClick={onBack}
              className={secondaryButtonStyles.className + " mb-4"}
              style={secondaryButtonStyles.style}
            >
              <div 
                className={secondaryButtonStyles.hoverOverlay}
                style={secondaryButtonStyles.hoverStyle}
              ></div>
              
              <div className={`relative z-10 px-4 py-2 flex items-center font-medium transition-all duration-300 ${
                isDarkMode ? 'text-white' : 'text-black'
              } drop-shadow-lg group-hover:drop-shadow-xl`}>
                <ArrowLeft className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:scale-110" />
                {t('admin.products.backToShops')}
              </div>
              
              <div className={secondaryButtonStyles.waveEffect}>
                <div 
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-800"
                  style={{
                    background: isDarkMode
                      ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                      : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)'
                  }}
                ></div>
              </div>
            </button>
            
            <div className="flex items-center space-x-3 mb-2">
              <div className="relative group/icon">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm"></div>
                <div className="relative bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl w-12 h-12 rounded-2xl flex items-center justify-center shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20">
                  <Package className="w-6 h-6 text-white/90 drop-shadow-lg" />
                  <div className="absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm"></div>
                </div>
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${isDarkMode ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent' : 'text-slate-900'}`}>
                  {t('admin.products.title')}
                </h2>
                <p className={`${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                  {shop.name} - {products.length} {t('catalogs.products')}
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={handleAddProduct}
            className={primaryButtonStyles.className}
            style={primaryButtonStyles.style}
          >
            <div 
              className={primaryButtonStyles.hoverOverlay}
              style={primaryButtonStyles.hoverStyle}
            ></div>
            
            <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg group-hover:drop-shadow-xl">
              <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:scale-110" />
              {t('admin.products.newProduct')}
            </div>
            
            <div className={primaryButtonStyles.waveEffect}>
              <div 
                className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-800"
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                    : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)'
                }}
              ></div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`${isDarkMode
      ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black'
      : 'bg-white'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderHeader()}
        
        <ProductList
          products={products}
          user={user}
          loading={loading}
          filteredProducts={filteredProducts}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          pFilters={pFilters}
          setPFilters={setPFilters}
          filterOptions={filterOptions}
          onEditProduct={handleEditProduct}
          onDeleteProduct={handleDeleteProduct}
        />
      </div>
    </div>
  );
};

export default InternalShopProductManagement;